import { useCallback, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import {
  FormSelectId,
  FormSelectIdProps,
  FormSelectInput,
  FormSelectInputNonFormProps,
  FormSelectInputProps,
} from '@nexelem/form-components';
import { useCustomTranslation } from '../../../hooks';
import { useWarehouses } from '../../../queryHooks/useWarehouses';
import { useStorages } from '../../../queryHooks/useStorages';

// eslint-disable-next-line no-undef
type FormWarehouseSelectIdProps = Omit<FormSelectIdProps, 'options' | 'placeholder'>;

export const FormWarehouseSelectId = ({ label, ...props }: FormWarehouseSelectIdProps) => {
  const { t, tWrapper } = useCustomTranslation('warehouse');
  const { data, isLoading } = useWarehouses();

  // TODO: Wrong BE type? Why id is optional? Remove || 1
  const options = data?.map((place) => {
    return { id: place.id || 0, label: place.name };
  });

  return (
    <FormSelectId
      {...props}
      label={label ?? tWrapper('warehouse')}
      loading={isLoading}
      options={options}
      placeholder={t('components.filters.searchOrSelect')}
    />
  );
};

type Option = NonNullable<ReturnType<typeof useStorages>['data']>[number];

type FormWarehouseSelectProps<T, Value extends FieldValues> = Pick<
  FormSelectInputNonFormProps,
  'maxWidth' | 'required' | 'placeholder' | 'label' | 'StartIcon' | 'showErrorMessage'
> &
  Pick<FormSelectInputProps<T, Value, T>, 'name' | 'control'> & {
    filterOptions?: (options: Option[]) => Option[];
  };

export const FormWarehouseSelect = <T, Value extends FieldValues>(props: FormWarehouseSelectProps<T, Value>) => {
  const [inputValue, setInputValue] = useState('');
  const [isInputOpen, setIsInputOpen] = useState(false);

  const onToggleOpen = useCallback(() => {
    setIsInputOpen((prevState) => !prevState);
  }, []);

  const { data, isFetching } = useStorages(inputValue, isInputOpen);

  return (
    <FormSelectInput
      {...props}
      control={props.control}
      getOptionKey={(value) => (value.value || '').toString()}
      getOptionLabel={(item) => item.key}
      inputValue={inputValue}
      isOptionEqualToValue={(optionA, optionB) => optionA.value === optionB.value}
      loading={isFetching}
      open={isInputOpen}
      options={data ?? []}
      onClose={onToggleOpen}
      onInputChange={setInputValue}
      onOpen={onToggleOpen}
    />
  );
};
